import * as Sentry from '@sentry/react'
import { Component, type ErrorInfo, type ReactNode } from 'react'
import { AlertTriangle } from 'lucide-react'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  message: string | null
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, message: null }

  static getDerivedStateFromError(error: unknown): State {
    const message = error instanceof Error ? error.message : 'An unexpected error occurred.'
    return { hasError: true, message }
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    console.error('[ErrorBoundary]', error, info.componentStack)
    Sentry.captureException(error, { extra: { componentStack: info.componentStack } })
  }

  handleReset = () => {
    this.setState({ hasError: false, message: null })
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback

      return (
        <div style={{
          minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'var(--bg)', padding: '2rem',
        }}>
          <div style={{
            background: 'var(--surface)', borderRadius: '12px',
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            padding: '2.5rem', maxWidth: '480px', width: '100%', textAlign: 'center',
          }}>
            <div style={{ marginBottom: '1rem' }}><AlertTriangle size={32} strokeWidth={1.5} style={{ color: 'var(--warning-text)' }} /></div>
            <h2 style={{ margin: '0 0 0.75rem', fontSize: '1.2rem', fontWeight: 700, color: 'var(--text-heading)' }}>
              Something went wrong
            </h2>
            <p style={{ margin: '0 0 1.5rem', fontSize: '0.9rem', color: 'var(--text-3)' }}>
              {this.state.message}
            </p>
            <button
              onClick={this.handleReset}
              style={{
                background: 'var(--accent)', color: 'var(--accent-contrast)', border: 'none',
                borderRadius: '6px', padding: '0.6rem 1.5rem',
                fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer',
              }}
            >
              Try again
            </button>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
